/**
 * MCP Settings Modal
 *
 * Allows users to generate, view, copy, and revoke API keys
 * for the MCP server. Uses ModalWrapper for the overlay.
 */
import React, { useState, useEffect, useCallback } from "react";
import ModalWrapper from "./Wrapper";
import {
  listApiKeys,
  createApiKey,
  deleteApiKey,
} from "services/McpApiKeys";

/**
 * MCP Settings modal content
 */
function McpSettingsContent({ onClose }) {
  // State
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newKeyName, setNewKeyName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newRawKey, setNewRawKey] = useState("");
  const [copied, setCopied] = useState(false);
  const [deletingHash, setDeletingHash] = useState(null);

  // Load keys on mount
  const loadKeys = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const result = await listApiKeys();
      setKeys(result);
    } catch (err) {
      setError(err.message || "Failed to load API keys");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  // Create key
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;

    try {
      setCreating(true);
      setError("");
      setNewRawKey("");
      const result = await createApiKey(newKeyName.trim());
      setNewRawKey(result.rawKey);
      setNewKeyName("");
      await loadKeys();
    } catch (err) {
      setError(err.message || "Failed to create key");
    } finally {
      setCreating(false);
    }
  };

  // Copy key to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(newRawKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = newRawKey;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Delete key
  const handleDelete = async (keyHash) => {
    if (!window.confirm("Revoke this API key? Any clients using it will stop working.")) {
      return;
    }

    try {
      setDeletingHash(keyHash);
      setError("");
      await deleteApiKey(keyHash);
      await loadKeys();
    } catch (err) {
      setError(err.message || "Failed to delete key");
    } finally {
      setDeletingHash(null);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="wotodo-mcp-settings">
      {/* Header */}
      <div className="wotodo-mcp-settings__header">
        <div>
          <h3 className="wotodo-mcp-settings__title">MCP Settings</h3>
          <p className="wotodo-mcp-settings__subtitle">
            Manage API keys for AI assistants and bots to access your notes via MCP.
          </p>
        </div>
        <button
          className="wotodo-mcp-settings__close"
          onClick={onClose}
          title="Close"
        >
          ×
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="wotodo-mcp-settings__error">
          {error}
        </div>
      )}

      {/* New key result (shown only once after creation) */}
      {newRawKey && (
        <div className="wotodo-mcp-settings__newkey">
          <p className="wotodo-mcp-settings__newkey-warning">
            <span role="img" aria-label="warning">⚠️</span> Copy this key now — it won't be shown again!
          </p>
          <div className="wotodo-mcp-settings__newkey-row">
            <code className="wotodo-mcp-settings__newkey-value">
              {newRawKey}
            </code>
            <button
              className="wotodo-mcp-settings__btn wotodo-mcp-settings__btn--copy"
              onClick={handleCopy}
            >
              {copied ? "✓ Copied!" : "Copy"}
            </button>
          </div>
        </div>
      )}

      {/* Create new key form */}
      <form className="wotodo-mcp-settings__create" onSubmit={handleCreate}>
        <input
          type="text"
          className="wotodo-mcp-settings__input"
          placeholder='Key name (e.g. "Cursor", "OpenClaw")'
          value={newKeyName}
          onChange={(e) => setNewKeyName(e.target.value)}
          disabled={creating}
          maxLength={50}
        />
        <button
          type="submit"
          className="wotodo-mcp-settings__btn wotodo-mcp-settings__btn--create"
          disabled={creating || !newKeyName.trim()}
        >
          {creating ? "Creating..." : "Generate Key"}
        </button>
      </form>

      {/* Keys list */}
      <div className="wotodo-mcp-settings__list">
        {loading ? (
          <p className="wotodo-mcp-settings__empty">Loading keys...</p>
        ) : keys.length === 0 ? (
          <p className="wotodo-mcp-settings__empty">
            No API keys yet. Create one to get started.
          </p>
        ) : (
          <table className="wotodo-mcp-settings__table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th>Created</th>
                <th>Last Used</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => (
                <tr key={key.keyHash}>
                  <td>{key.name}</td>
                  <td>
                    <code>{key.keyPrefix}•••••••</code>
                  </td>
                  <td>{formatDate(key.created_at)}</td>
                  <td>{formatDate(key.last_used)}</td>
                  <td>
                    <button
                      className="wotodo-mcp-settings__btn wotodo-mcp-settings__btn--delete"
                      onClick={() => handleDelete(key.keyHash)}
                      disabled={deletingHash === key.keyHash}
                      title="Revoke key"
                    >
                      {deletingHash === key.keyHash ? "..." : "Revoke"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Connection info */}
      <div className="wotodo-mcp-settings__info">
        <h4>How to connect</h4>
        <p>Use this MCP endpoint in your AI client:</p>
        <code className="wotodo-mcp-settings__endpoint">
          https://app.seeker.place/mcp
        </code>
        <p>
          Set the API key as a Bearer token in the Authorization header.
        </p>
      </div>
    </div>
  );
}

/**
 * MCP Settings Modal (wraps content in ModalWrapper)
 */
export function McpSettingsModal({ isOpen, modalClose }) {
  return (
    <ModalWrapper
      isOpen={isOpen}
      modalClose={modalClose}
      content={<McpSettingsContent onClose={modalClose} />}
    />
  );
}

export default McpSettingsModal;
